<template>
  <div class="tw-max-h-full">
    <GraphqlDataTable
      v-model:selected-rows="selectedRows"
      v-model:filters="defaultFilters"
      :query
      :order-by="initialOrder"
      :search-placeholder="$t('Search job posts')"
      :filter-black-list="['accounts', 'jobOwners']"
      :quick-filters="['jobOwners']"
      :max-height-adjustment="60"
      :bulk-actions="jobBulkActions"
      @full-screen="fullscreen = $event"
    >
      <template #action="{ row }">
        <JobActionColumn :row="row" />
      </template>
    </GraphqlDataTable>
    <BaseConfirmModal />
  </div>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { computed, ref } from 'vue'
import { WdsIconNameEnum } from '@worksome/wds-components'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import { graphql } from '@/gql'
import JobActionColumn from '@/components/GraphqlDataTable/columns/Job/JobActionColumn.vue'
import { JobOrderByColumn, JobStatus, SortOrder } from '@/gql/graphql'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { $t } from '@/composables/translation'
import { endJobsWithConfirmation } from '@/components/Job/functions/endJobs'
import BaseConfirmModal from '@/components/BaseConfirmModal.vue'

const fullscreen = ref(false)
const selectedRows = ref<Record<string, object>>({})

const defaultFilters = ref<Record<string, any>>({
  status: [JobStatus.Draft, JobStatus.Active],
})

const hasEligibleStatus = (row: any): boolean =>
  row?.status !== JobStatus.Completed && row?.status !== JobStatus.Removed

const hasEligibleStatusJobSelected = computed(() =>
  Object.values(selectedRows.value).some((row) => hasEligibleStatus(row)),
)

const hasJobWithPermissionsSelected = computed(() =>
  Object.values(selectedRows.value).some((row) => (row as any)?.viewerCanEdit),
)

const hasEndableJobSelected = computed(() =>
  Object.values(selectedRows.value).some(
    (row) => hasEligibleStatus(row) && (row as any)?.viewerCanEdit,
  ),
)

const query = graphql(`
  query JobsContractsQuery(
    $accounts: [ID!]
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [JobsOrderByClauseInput!]
    $jobOwners: [ID!]
    $hasBids: Boolean
    $hasHire: Boolean
    $startDateRange: DateRangeInput
    $endDateRange: DateRangeInput
    $jobRateTypes: [RateType!]
    $locationPreferences: [LocationPreference!]
    $commitment: [Association!]
    $skills: [String!]
    $seniority: [ExpectedExperienceLevel!]
    $customFields: [CustomFieldTypeValueInput!]
    $status: [JobStatus!]
    $unfilled: Boolean
  ) {
    jobs(
      accounts: $accounts
      search: $search
      page: $page
      first: $first
      hasBids: $hasBids
      hasHire: $hasHire
      rateTypes: $jobRateTypes
      associations: $commitment
      locationPreferences: $locationPreferences
      owners: $jobOwners
      skills: $skills
      experienceLevel: $seniority
      orderBy: $orderBy
      customFields: $customFields
      startDateRange: $startDateRange
      endDateRange: $endDateRange
      isJobPost: true
      unfilled: $unfilled
      statuses: $status
    ) {
      data {
        ...JobCreatedAtColumn
        ...JobSharedChannelsColumn
        ...JobStatusColumn
        ...JobHireAndPoNumberColumn
        ...JobStartDateColumn
        ...JobEndDateColumn
        ...JobCandidatesBidsHiresColumn
        ...JobLastCandidateSubmittedColumn
        ...RateColumnJob
        ...JobOwnersColumn
        ...JobActionColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const initialOrder = {
  field: JobOrderByColumn.CreatedAt,
  order: SortOrder.Desc,
}

const jobBulkActions = computed<DataTableBulkAction[]>(() => [
  {
    name: $t('End jobs'),
    icon: WdsIconNameEnum.CLOSE_OUTLINE,
    type: 'error',
    disabled: !hasEndableJobSelected.value,
    tooltip: hasEndableJobSelected.value
      ? undefined
      : !hasEligibleStatusJobSelected.value
        ? $t('Select at least one draft or active job to end')
        : !hasJobWithPermissionsSelected.value
          ? $t('You do not have permissions for any job selected')
          : $t('Select at least one draft or active job you have permission to edit, to end'),
    action: (rows) => {
      endJobsWithConfirmation(Object.values(rows).map((row) => (row as any)?.id))
    },
  },
])
</script>
